import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@buildex/ui';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { userInvited } from '@/entities/tenant/model';
import type { AdminTenant } from '@/entities/tenant/types';

interface TenantUsersProps {
  tenant: AdminTenant;
}

export function TenantUsers({ tenant }: TenantUsersProps) {
  const { t } = useTranslation();
  const [modalOpened, setModalOpened] = useState(false);
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string | null>(tenant.roles[0]?.id ?? null);
  const onInvite = useUnit(userInvited);

  const roleName = (id: string) => tenant.roles.find((r) => r.id === id)?.name ?? id;

  const submit = () => {
    if (!email || roleId === null) return;
    onInvite({ tenantId: tenant.id, email, roleId });
    setEmail('');
    setModalOpened(false);
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {t('users.hint')}
        </Text>
        <Button size="xs" onClick={() => setModalOpened(true)}>
          {t('users.invite')}
        </Button>
      </Group>

      <Card p={0}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('users.email')}</Table.Th>
              <Table.Th>{t('users.name')}</Table.Th>
              <Table.Th>{t('users.role')}</Table.Th>
              <Table.Th w={120}>{t('users.status')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tenant.users.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    {t('users.empty')}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              tenant.users.map((user) => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Text size="sm">{user.email}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{user.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">{roleName(user.roleId)}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={user.status === 'active' ? 'teal' : 'yellow'}>
                      {t(`users.${user.status}`)}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title={t('users.inviteTitle')}>
        <Stack gap="sm">
          <TextInput
            label={t('users.email')}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <Select
            label={t('users.role')}
            data={tenant.roles.map((r) => ({ value: r.id, label: r.name }))}
            value={roleId}
            onChange={setRoleId}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setModalOpened(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={!email || roleId === null}>
              {t('users.send')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
